import { useEffect, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  CheckCircle2,
  Clock,
  CreditCard,
  Loader2,
  XCircle,
  RefreshCw,
  Package,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { formatEuro } from "@/lib/catalog-format";
import {
  getPaymentStatus,
  type PublicPaymentStatus,
} from "@/lib/payment-status.functions";

export const Route = createFileRoute("/betaling/$paymentId")({
  head: ({ params }) => ({
    meta: [
      { title: `Betaalstatus ${params.paymentId} — Elvion Group Nederland` },
      { name: "robots", content: "noindex,nofollow" },
      {
        name: "description",
        content:
          "Live status van je Mollie-betaling en bijhorende bestelling bij Elvion Group Nederland.",
      },
    ],
  }),
  component: PaymentStatusPage,
  errorComponent: ({ error }) => (
    <ErrorShell message={(error as Error)?.message ?? "Onbekende fout"} />
  ),
  notFoundComponent: () => (
    <ErrorShell message="Deze betaling kon niet worden gevonden." />
  ),
});

const PAY_LABEL: Record<string, string> = {
  open: "Wacht op betaling",
  pending: "Betaling in behandeling",
  authorized: "Geautoriseerd",
  paid: "Betaald",
  failed: "Mislukt",
  expired: "Verlopen",
  canceled: "Geannuleerd",
};

const ORDER_LABEL: Record<string, string> = {
  pending: "In behandeling",
  paid: "Betaald",
  processing: "Wordt verwerkt",
  shipped: "Verzonden",
  delivered: "Bezorgd",
  cancelled: "Geannuleerd",
  completed: "Afgerond",
};

const TERMINAL = new Set(["paid", "failed", "expired", "canceled"]);

function tone(status: string): "ok" | "bad" | "warn" {
  if (status === "paid") return "ok";
  if (status === "failed" || status === "expired" || status === "canceled") return "bad";
  return "warn";
}

function PaymentStatusPage() {
  const { paymentId } = Route.useParams();
  const fetchStatus = useServerFn(getPaymentStatus);

  const query = useQuery<PublicPaymentStatus>({
    queryKey: ["payment-status", paymentId],
    queryFn: () => fetchStatus({ data: { paymentId } }),
    refetchInterval: (q) => {
      const s = q.state.data?.mollie?.status ?? q.state.data?.payment_status;
      if (s && TERMINAL.has(s)) return false;
      return 3000;
    },
    refetchOnWindowFocus: true,
    retry: 1,
  });

  // Auto-refetch when tab becomes visible again
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") query.refetch();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [query]);

  if (query.isLoading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-20 text-text-secondary">
          <Loader2 className="mr-2 size-5 animate-spin" />
          Betaalstatus ophalen…
        </div>
      </Shell>
    );
  }

  if (query.isError || !query.data) {
    return (
      <ErrorShell
        message={
          (query.error as Error)?.message ??
          "Geen betaling gevonden met dit id."
        }
      />
    );
  }

  return <StatusView data={query.data} isFetching={query.isFetching} onRefresh={() => query.refetch()} />;
}

function StatusView({
  data,
  isFetching,
  onRefresh,
}: {
  data: PublicPaymentStatus;
  isFetching: boolean;
  onRefresh: () => void;
}) {
  const effective = data.mollie?.status ?? data.payment_status;
  const t = tone(effective);
  const Icon = t === "ok" ? CheckCircle2 : t === "bad" ? XCircle : Clock;

  const headline = useMemo(() => {
    if (effective === "paid") return "Betaling ontvangen ✓";
    if (effective === "failed") return "Betaling mislukt";
    if (effective === "expired") return "Betaling verlopen";
    if (effective === "canceled") return "Betaling geannuleerd";
    if (effective === "pending") return "Betaling wordt verwerkt…";
    if (effective === "authorized") return "Betaling geautoriseerd";
    return "Wachten op betaling…";
  }, [effective]);

  const sub = useMemo(() => {
    if (effective === "paid")
      return "Je bestelling is bevestigd. Je ontvangt zo de bevestigingsmail.";
    if (effective === "failed")
      return (
        data.mollie?.failureReason ??
        "De betaling is door de bank geweigerd. Probeer het opnieuw of kies een andere methode."
      );
    if (effective === "expired")
      return "De betaallink is verlopen. Plaats je bestelling opnieuw.";
    if (effective === "canceled")
      return "Je hebt de betaling geannuleerd.";
    return "Deze pagina ververst automatisch zodra er nieuws is.";
  }, [effective, data.mollie?.failureReason]);

  const isTerminal = TERMINAL.has(effective);

  return (
    <Shell>
      <article
        className={`rounded-2xl border p-6 shadow-sm ${
          t === "ok"
            ? "border-green-200 bg-green-50"
            : t === "bad"
              ? "border-red-200 bg-red-50"
              : "border-amber-200 bg-amber-50"
        }`}
      >
        <div className="flex items-start gap-4">
          <Icon
            className={`size-10 shrink-0 ${
              t === "ok"
                ? "text-green-600"
                : t === "bad"
                  ? "text-red-600"
                  : "text-amber-600"
            }`}
          />
          <div className="flex-1">
            <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
              {headline}
            </h1>
            <p className="mt-1 text-sm text-text-secondary">{sub}</p>
          </div>
          {!isTerminal && (
            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-text-secondary shadow-sm">
              <span className="relative flex size-2">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex size-2 rounded-full bg-amber-500" />
              </span>
              Live
            </span>
          )}
        </div>
      </article>

      <section className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <CreditCard className="size-4" /> Betaling
          </div>
          <dl className="mt-3 space-y-2 text-sm">
            <Row k="Status" v={PAY_LABEL[effective] ?? effective} />
            {data.mollie?.method && <Row k="Methode" v={data.mollie.method} />}
            {data.mollie?.amount && (
              <Row
                k="Bedrag"
                v={`${data.mollie.amount.currency} ${data.mollie.amount.value}`}
              />
            )}
            {data.mollie?.paidAt && (
              <Row
                k="Betaald op"
                v={new Date(data.mollie.paidAt).toLocaleString("nl-NL")}
              />
            )}
            {data.mollie?.failureReason && (
              <Row k="Reden" v={data.mollie.failureReason} />
            )}
          </dl>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Package className="size-4" /> Bestelling
          </div>
          <dl className="mt-3 space-y-2 text-sm">
            <Row k="Ordernummer" v={data.order_number} />
            <Row k="Orderstatus" v={ORDER_LABEL[data.order_status] ?? data.order_status} />
            <Row k="Totaal" v={formatEuro(data.total_cents)} />
            {data.amount_paid_cents > 0 && (
              <Row k="Reeds betaald" v={formatEuro(data.amount_paid_cents)} />
            )}
            <Row
              k="Geplaatst"
              v={new Date(data.created_at).toLocaleString("nl-NL")}
            />
          </dl>
        </div>
      </section>

      <div className="mt-6 flex flex-wrap gap-3">
        {!isTerminal && (
          <Button variant="outline" onClick={onRefresh} disabled={isFetching}>
            <RefreshCw className={`mr-2 size-4 ${isFetching ? "animate-spin" : ""}`} />
            Nu vernieuwen
          </Button>
        )}
        <Button asChild variant="outline">
          <Link to="/order-status">Volledig statusoverzicht openen</Link>
        </Button>
        {effective === "failed" || effective === "expired" ? (
          <Button asChild>
            <Link to="/">Terug naar de winkel</Link>
          </Button>
        ) : null}
      </div>
    </Shell>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-text-secondary">{k}</dt>
      <dd className="text-right font-semibold">{v}</dd>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Header />
      <main className="bg-background">
        <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:py-16">
          {children}
        </section>
      </main>
      <Footer />
    </>
  );
}

function ErrorShell({ message }: { message: string }) {
  return (
    <Shell>
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800">
        <h1 className="font-display text-xl font-bold">Betaling niet gevonden</h1>
        <p className="mt-2 text-sm">{message}</p>
        <div className="mt-4 flex gap-3">
          <Button asChild variant="outline">
            <Link to="/order-status">Zoek via ordernummer</Link>
          </Button>
          <Button asChild>
            <Link to="/">Naar de winkel</Link>
          </Button>
        </div>
      </div>
    </Shell>
  );
}
